import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import axios from 'axios';
import { UserContext } from '../Context';
import { ReportContext } from '../Context';
import { ThemeContext } from '../Context';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';
// Force le chargement local de TinyMCE v6
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce-i18n/langs/fr_FR';

// Plugins COMMUNITY uniquement (rien de premium)
import 'tinymce/plugins/lists';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/code';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/image';
import 'tinymce/plugins/table';

// CSS de skin/ui en local pour afficher l'UI sans chargement externe
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';

import { Editor } from '@tinymce/tinymce-react';
import { 
  Box,
  Typography, 
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  FormHelperText,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// Configuration TinyMCE pour mode self-hosted gratuit
const buildTinymceConfig = (token) => ({
  menubar: false,
  language: 'fr_FR',
  plugins: [
    'lists', 'autoresize', 'wordcount', 'fullscreen', 'table', 'searchreplace', 'image'
  ],
  toolbar: 'undo redo | bold italic underline strikethrough | forecolor backcolor | bullist numlist blockquote | alignleft aligncenter alignright alignjustify | indent outdent | table | image | removeformat searchreplace | fullscreen',
  content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 15px; }',
  branding: false,
  elementpath: false,
  statusbar: false,
  paste_as_text: false,
  paste_webkit_styles: 'none',
  paste_remove_styles: false,
  paste_merge_formats: true,
  smart_paste: false,
  // Configuration pour mode self-hosted (pas de cloud)
  promotion: false,
  contextmenu: false,
  // Évite tout chargement externe
  skin: false,
  content_css: false,
  // Fullscreen natif du navigateur pour éviter les problèmes de z-index
  fullscreen_native: true,
  // Hauteur minimale souhaitée (~5 lignes)
  min_height: 200,
  // Auto-resize
  autoresize_min_height: 200,
  autoresize_max_height: 600,
  autoresize_bottom_margin: 16,
  images_upload_credentials: true,
  file_picker_types: 'image',
  image_description: false,
  images_upload_handler: async (blobInfo) => {
    const formData = new FormData();
    formData.append('image', blobInfo.blob(), blobInfo.filename());

    try {
      const response = await axios.post('/api/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        withCredentials: true
      });

      const url = response.data?.url;
      if (!url) {
        throw new Error("Réponse inattendue du serveur d'upload.");
      }

      return url;
    } catch (error) {
      const message = error.response?.data?.message || "Échec de l'upload de l'image.";
      throw new Error(message);
    }
  },
  paste_postprocess: (plugin, args) => {
    // Normalisation des espaces comme dans Quill
    args.node.innerHTML = args.node.innerHTML
      .replace(/<strong>([^<]+)<\/strong>\s+<strong>([^<]+)<\/strong>/g, '<strong>$1&nbsp;$2</strong>')
      .replace(/<strong><u>([^<]+)<\/u><\/strong>\s+<strong>([^<]+)<\/strong>/g, '<strong><u>$1</u>&nbsp;$2</strong>')
      .replace(/<u><strong>([^<]+)<\/strong><\/u>\s+<strong>([^<]+)<\/strong>/g, '<strong><u>$1</u>&nbsp;$2</strong>');
  }
});

// Normalisation des espaces entre balises inline pour éviter que Quill ne les colle
const normalizeEditorHtmlSpaces = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  let normalized = html;
  // Fusionner deux blocs strong avec un espace insécable unique
  normalized = normalized.replace(
    /<strong>([^<]+)<\/strong>\s+<strong>([^<]+)<\/strong>/g,
    '<strong>$1&nbsp;$2</strong>'
  );
  // Cas: <strong><u>NOM</u></strong> <strong>Prénom</strong> => <strong><u>NOM</u>&nbsp;Prénom</strong>
  normalized = normalized.replace(
    /<strong><u>([^<]+)<\/u><\/strong>\s+<strong>([^<]+)<\/strong>/g,
    '<strong><u>$1</u>&nbsp;$2</strong>'
  );
  // Cas: <u><strong>NOM</strong></u> <strong>Prénom</strong> => <strong><u>NOM</u>&nbsp;Prénom</strong>
  normalized = normalized.replace(
    /<u><strong>([^<]+)<\/strong><\/u>\s+<strong>([^<]+)<\/strong>/g,
    '<strong><u>$1</u>&nbsp;$2</strong>'
  );
  return normalized;
};

const isSeparatorOption = (option = '') => {
  if (typeof option !== 'string') return false;
  const trimmed = option.trim();
  return trimmed.startsWith('---') && trimmed.endsWith('---');
};

const getSelectableOptions = (options = []) => {
  if (!Array.isArray(options)) return [];
  return options.filter(option => !isSeparatorOption(option));
};

const normalizeAutocompleteValue = (value, multiple) => {
  const cleanValue = (item) => (typeof item === 'string' ? item.trim() : item);

  if (multiple) {
    return Array.isArray(value)
      ? value.map(cleanValue).filter(Boolean)
      : (value ? [cleanValue(value)].filter(Boolean) : []);
  }
  return cleanValue(value) || '';
};

function SearchableSelectField({
  field,
  name,
  value,
  onValueChange,
  hasError,
  helperText,
  fieldSize
}) {
  const multiple = Boolean(field.multiple);
  const normalizedValue = multiple
    ? (Array.isArray(value) ? value : [])
    : (value || null);

  return (
    <Autocomplete
      multiple={multiple}
      freeSolo={Boolean(field.freeSolo)}
      options={getSelectableOptions(field.options)}
      value={normalizedValue}
      disableCloseOnSelect={multiple}
      autoSelect={Boolean(field.freeSolo)}
      noOptionsText="Aucune option"
      clearText="Effacer"
      openText="Ouvrir"
      closeText="Fermer"
      getOptionLabel={(option) => (typeof option === 'string' ? option : String(option || ''))}
      isOptionEqualToValue={(option, selected) => option === selected}
      onChange={(event, nextValue) => {
        onValueChange(normalizeAutocompleteValue(nextValue, multiple));
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          {multiple && (
            <Checkbox
              checked={selected}
              size={fieldSize === 'small' ? 'small' : 'medium'}
              sx={{ mr: 1, py: 0 }}
            />
          )}
          {option}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={field.label}
          margin="dense"
          size={fieldSize}
          required={field.required}
          error={hasError}
          helperText={helperText}
        />
      )}
    />
  );
}

// Composant pour afficher l'indicateur "Facultatif"
function OptionalBadge({ isDark }) {
  return (
    <Chip
      label="Facultatif"
      size="small"
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        height: 20,
        fontSize: '0.7rem',
        backgroundColor: isDark ? '#616161' : '#757575',
        color: 'white',
        fontWeight: 500,
        zIndex: 10,
        '& .MuiChip-label': {
          px: 1,
          py: 0.25
        }
      }}
    />
  );
}

// Composant personnalisé pour l'éditeur TinyMCE
function RichTextEditor({ value, onChange, placeholder, isDark }) {
  const handleEditorChange = (content, editor) => {
    // Appliquer la normalisation des espaces
    const normalizedContent = normalizeEditorHtmlSpaces(content);
    onChange(normalizedContent);
  };

  const editorConfig = {
    ...buildTinymceConfig(sessionStorage.getItem('authToken')),
    placeholder: placeholder || "Écrivez ici le contenu de la rubrique...",
    content_style: `
      body {
        font-family: Helvetica, Arial, sans-serif;
        font-size: 15px;
        color: ${isDark ? '#ecf0f1' : 'inherit'};
        background-color: ${isDark ? '#22303e' : '#ffffff'};
      }
      .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
        color: ${isDark ? '#95a5a6' : '#aaa'};
      }
    `
  };

  return (
    <Box sx={{
      '& .tox-tinymce': {
        borderRadius: '4px',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
        minHeight: '120px'
      },
      '& .tox-toolbar': {
        backgroundColor: isDark ? '#21303c' : '#f5f5f5',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
      },
      '& .tox-editor-container': {
        backgroundColor: isDark ? '#22303e' : '#ffffff',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
      },
      mt: 0.5,
      mb: 2
    }}>
      <Editor
        value={normalizeEditorHtmlSpaces(value) || ''}
        init={editorConfig}
        onEditorChange={handleEditorChange}
      />
    </Box>
  );
}

function RubricForm({ rubric, onSave, reportNumber, reportYear }) {
  const { user } = useContext(UserContext);
  const { report } = useContext(ReportContext);
  const { theme } = useContext(ThemeContext);
  
  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';
  
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionErrorDetails, setActionErrorDetails] = useState(null);
  const [rawFormFields, setRawFormFields] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [generatingContent, setGeneratingContent] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [insertValues, setInsertValues] = useState({});
  const [reportDetails, setReportDetails] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [modifiedFields, setModifiedFields] = useState(new Set());
  const loadedVersionRef = useRef(null);

  const draftStorageKey = useMemo(() => {
    if (!rubric) return null;
    const rubricKey = rubric.id || rubric.version || 'unknown';
    const reportKey = `${reportNumber || 'unknown'}-${reportYear || 'unknown'}`;
    return `rubricDraft:${reportKey}:${rubricKey}`;
  }, [rubric, reportNumber, reportYear]);

  const formConfigStorageKey = useMemo(() => {
    if (!rubric?.version) return null;
    return `rubricFormConfig:${rubric.version}`;
  }, [rubric?.version]);

  const loadDraft = () => {
    if (!draftStorageKey) return null;
    try {
      const raw = sessionStorage.getItem(draftStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn('Draft invalide, suppression:', err);
      sessionStorage.removeItem(draftStorageKey);
      return null;
    }
  };

  const persistDraft = (values, inserts, modified) => {
    if (!draftStorageKey) return;
    try {
      const filteredValues = Object.keys(values || {}).reduce((acc, key) => {
        if (!key.startsWith('_')) {
          acc[key] = values[key];
        }
        return acc;
      }, {});
      const payload = {
        formValues: filteredValues,
        insertValues: inserts || {},
        modifiedFields: Array.from(modified || [])
      };
      sessionStorage.setItem(draftStorageKey, JSON.stringify(payload));
    } catch (err) {
      console.warn('Échec sauvegarde brouillon:', err);
    }
  };

  const clearDraft = () => {
    if (!draftStorageKey) return;
    sessionStorage.removeItem(draftStorageKey);
  };

  const loadCachedFormConfig = () => {
    if (!formConfigStorageKey) return null;
    try {
      const raw = sessionStorage.getItem(formConfigStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn('FormConfig invalide, suppression:', err);
      sessionStorage.removeItem(formConfigStorageKey);
      return null;
    }
  };

  const cacheFormConfig = (config) => {
    if (!formConfigStorageKey || !Array.isArray(config)) return;
    try {
      sessionStorage.setItem(formConfigStorageKey, JSON.stringify(config));
    } catch (err) {
      console.warn('Échec sauvegarde formConfig:', err);
    }
  };
  
  // Récupérer la configuration du formulaire au chargement
  useEffect(() => {
    const fetchFormConfig = async () => {
      if (rubric?.version && loadedVersionRef.current === rubric.version && formFields.length > 0) {
        return;
      }
      const initializeFormState = (config) => {
        const initialValues = {};
        const initialInsertValues = {};

        config.forEach(field => {
          if (field.type === 'checkbox') {
            initialValues[field.name] = field.defaultValue !== undefined ? field.defaultValue : false;
          } else if (field.type === 'insert') {
            initialValues[field.name] = field.defaultValue || [];
            initialInsertValues[field.name] = '';
          } else {
            initialValues[field.name] = field.defaultValue !== undefined ? field.defaultValue : '';
          }
        });

        const savedDraft = loadDraft();
        const hasDraftValues = savedDraft && savedDraft.formValues;
        if (hasDraftValues) {
          const draftValues = Object.keys(initialValues).reduce((acc, key) => {
            if (savedDraft.formValues[key] !== undefined) {
              acc[key] = savedDraft.formValues[key];
            }
            return acc;
          }, {});
          setFormValues({ ...initialValues, ...draftValues });
          setInsertValues(savedDraft.insertValues || initialInsertValues);
          setModifiedFields(new Set(savedDraft.modifiedFields || []));
        } else {
          setFormValues(initialValues);
          setInsertValues(initialInsertValues);
        }
      };

      if (!rubric || !rubric.version) {
        setLoadError("Version de la rubrique non spécifiée");
        setLoading(false);
        return;
      }
      
      try {
        if (!formFields || formFields.length === 0) {
          setLoading(true);
        }
        const token = sessionStorage.getItem('authToken');
        const response = await axios.get(`/api/rubric/form/${rubric.version}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setRawFormFields(response.data);
        setFormFields(response.data);
        cacheFormConfig(response.data);
        loadedVersionRef.current = rubric.version;
        initializeFormState(response.data);
        setLoadError(null);
        setLoading(false);
      } catch (error) {
        console.error("Erreur lors de la récupération de la configuration du formulaire:", error);
        const cachedConfig = loadCachedFormConfig();
        if (Array.isArray(cachedConfig) && cachedConfig.length > 0) {
          setRawFormFields(cachedConfig);
          setFormFields(cachedConfig);
          loadedVersionRef.current = rubric.version;
          initializeFormState(cachedConfig);
          setLoadError("Connexion au serveur interrompue. Formulaire chargé depuis le cache local.");
        } else {
          setLoadError("Impossible de charger le formulaire. Veuillez réessayer.");
        }
        setLoading(false);
      }
    };
    
    fetchFormConfig();
  }, [rubric?.version]);

  // Sauvegarder automatiquement le brouillon
  useEffect(() => {
    if (loading) return;
    persistDraft(formValues, insertValues, modifiedFields);
  }, [formValues, insertValues, modifiedFields, loading]);
  
  // Charger les détails du rapport pour peupler les selects reportItems
  useEffect(() => {
    if (!reportNumber || !reportYear) return;
    const fetchReportDetails = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const res = await axios.get(
          '/api/report/search',
          {
            params: { number: reportNumber, year: reportYear },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setReportDetails(res.data);
      } catch (err) {
        console.error("Erreur lors de la récupération du rapport:", err);
      }
    };
    fetchReportDetails();
  }, [reportNumber, reportYear]);

  // Charger la liste complète des utilisateurs pour les champs intervenant
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const res = await axios.get(
          '/api/user',
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setAllUsers(res.data);
      } catch (err) {
        console.error("Erreur lors de la récupération des utilisateurs:", err);
      }
    };
    fetchAllUsers();
  }, []);

  // Injecter les valeurs de base du rapport dans formValues
  useEffect(() => {
    if (!reportDetails) return;
    setFormValues(prev => ({
      ...prev,
      reportType: reportDetails.reportType,
      indice:     reportDetails.indice,
      number:     reportDetails.number,
      year:       reportDetails.year,
      unit:       reportDetails.unit,
    }));
  }, [reportDetails]);

  // Fonction pour convertir les grades en texte complet
  const convertGrade = (gradeCode) => {
    const gradeMapping = {
      'AAGP': 'Aspirant Agent de Police',
      'AGP': 'Agent de Police',
      '1AGP': 'Premier Agent de Police',
      'ADAGP': 'Aspirant Agent de sécurisation de Police',
      'DAGP': 'Agent de sécurisation de Police',
      '1DAGP': 'Premier Agent de sécurisation de Police',
      'ABASP': 'Aspirant Assistant de Sécurisation de Police',
      'BASP': 'Assistant de Sécurisation de Police',
      '1BASP': 'Premier Assistant de Sécurisation de Police',
      'BCSP': 'Coordonnateur de Sécurisation de Police',
      '1BCSP': 'Premier Coordonnateur de Sécurisation de Police',
      'AINP': 'Aspirant Inspecteur de Police',
      'INP': 'Inspecteur de Police',
      '1INP': 'Premier Inspecteur de Police',
      'AINPP': 'Aspirant Inspecteur Principal de Police',
      'INPP': 'Inspecteur Principal de Police',
      '1INPP': 'Premier Inspecteur Principal de Police',
      'ACP': 'Aspirant Commissaire de Police',
      'CP': 'Commissaire de Police',
      '1CP': 'Premier Commissaire de Police',
      'CDP': 'Commissaire Divisionnaire de Police',
      '1CDP': 'Premier Commissaire Divisionnaire de Police'
    };
    return gradeMapping[gradeCode] || gradeCode;
  };

  // Modifier `useEffect` qui met à jour les options des selects
  useEffect(() => {
    if (!rawFormFields || rawFormFields.length === 0) return;
    const updated = rawFormFields.map(field => {
      if (!reportDetails) return field;
      
      // Extraire le type de base du nom du champ (ex: "intervenant1" -> "intervenant")
      const baseType = field.name.replace(/[0-9]+$/, '');
      
      // Traiter les champs standards
      let updatedField = processDynamicOptions(field, baseType);
      
      // Traiter les champs dans multipleItems
      if (field.type === 'multipleItems' && field.fields && Array.isArray(field.fields)) {
        const updatedFields = field.fields.map(subField => {
          const subBaseType = subField.name.replace(/[0-9]+$/, '');
          return processDynamicOptions(subField, subBaseType);
        });
        updatedField = { ...updatedField, fields: updatedFields };
      }
      
      return updatedField;
    });
    setFormFields(updated);
  }, [rawFormFields, reportDetails, allUsers]);
  
  // Fonction pour traiter les options dynamiques des champs
  const processDynamicOptions = (field, baseType) => {
    if (!reportDetails) return field;
    
    switch (baseType) {
      case 'intervenant':
        // Stocker les données complètes des intervenants du rapport
        const intervenantsOptions = reportDetails.intervenants.map(i => {
          const displayText = `${i.lastname.toUpperCase()} ${i.firstname}, ${i.rank}`;
          return {
            value: displayText,
            data: i,
            isReportIntervenant: true
          };
        }).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
        
        // Ajouter les autres utilisateurs s'ils sont disponibles
        const allOptions = [...intervenantsOptions];
        
        if (allUsers && allUsers.length > 0) {
          // Ajouter un séparateur
          allOptions.push({
            value: '--- Autres collègues ---',
            data: null,
            isSeparator: true
          });
          
          // Filtrer les utilisateurs qui ne sont pas déjà dans les intervenants du rapport
          const reportIntervenantIds = reportDetails.intervenants.map(i => i.userId).filter(Boolean);
          const otherUsers = allUsers.filter(user => !reportIntervenantIds.includes(user._id));
          
          // Ajouter les autres utilisateurs
          const usersOptions = otherUsers.map(user => {
            const grade = convertGrade(user.rank || '');
            const displayText = `${user.lastname.toUpperCase()} ${user.firstname}, ${grade}`;
            return {
              value: displayText,
              data: {
                isExternal: false,
                premier: false,
                sign: false,
                rank: grade,
                lastname: user.lastname,
                firstname: user.firstname,
                userId: user._id
              },
              isOtherUser: true
            };
          }).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
          
          allOptions.push(...usersOptions);
        }
        
        // Mettre à jour les valeurs du rapport pour inclure toutes les données
        setFormValues(prev => ({
          ...prev,
          _intervenantsData: allOptions.reduce((acc, option) => {
            if (!option.isSeparator) {
              acc[option.value] = option;
            }
            return acc;
          }, {})
        }));
        
        // Retourner uniquement les valeurs d'affichage pour le select
        return { ...field, options: allOptions.map(option => option.value) };
        
      case 'opj':
        // Stocker les données complètes des intervenants du rapport ayant la qualité OPJ
        const intervenantsOPJOptions = reportDetails.intervenants
          .filter(i => i.isOPJ)
          .map(i => {
          const displayText = `${i.lastname.toUpperCase()} ${i.firstname}, ${i.rank}`;
          return {
            value: displayText,
            data: i,
            isReportIntervenant: true
          };
        }).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
        
        // Ajouter les options pour OPJ
        const allOPJOptions = [...intervenantsOPJOptions];
        
        if (allUsers && allUsers.length > 0) {
          // Ajouter un séparateur
          allOPJOptions.push({
            value: '--- Autres Officiers de Police Judiciaire ---',
            data: null,
            isSeparator: true
          });
          
          // Filtrer les utilisateurs OPJ qui ne sont pas déjà dans les intervenants du rapport
          const reportIntervenantIds = reportDetails.intervenants.map(i => i.userId).filter(Boolean);
          const opjUsers = allUsers.filter(user => user.opj === true && !reportIntervenantIds.includes(user._id));
          
          // Ajouter les utilisateurs OPJ
          const opjUsersOptions = opjUsers.map(user => {
            const grade = convertGrade(user.rank || '');
            const displayText = `${user.lastname.toUpperCase()} ${user.firstname}, ${grade}`;
            return {
              value: displayText,
              data: {
                isExternal: false,
                premier: false,
                sign: false,
                isOPJ: true,
                isOPA: !!user.opa,
                rank: grade,
                lastname: user.lastname,
                firstname: user.firstname,
                userId: user._id
              },
              isOPJUser: true
            };
          }).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
          
          allOPJOptions.push(...opjUsersOptions);
        }
        
        // Mettre à jour les valeurs du rapport pour inclure toutes les données OPJ
        setFormValues(prev => ({
          ...prev,
          _opjData: allOPJOptions.reduce((acc, option) => {
            if (!option.isSeparator) {
              acc[option.value] = option;
            }
            return acc;
          }, {})
        }));
        
        // Retourner uniquement les valeurs d'affichage pour le select
        return { ...field, options: allOPJOptions.map(option => option.value) };
        
      case 'opa':
        // Stocker les données complètes des intervenants du rapport ayant la qualité OPA
        const intervenantsOPAOptions = reportDetails.intervenants
          .filter(i => i.isOPA)
          .map(i => {
          const displayText = `${i.lastname.toUpperCase()} ${i.firstname}, ${i.rank}`;
          return {
            value: displayText,
            data: i,
            isReportIntervenant: true
          };
        }).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
        
        // Ajouter les options pour OPA
        const allOPAOptions = [...intervenantsOPAOptions];
        
        if (allUsers && allUsers.length > 0) {
          // Ajouter un séparateur
          allOPAOptions.push({
            value: '--- Autres Officiers de Police Administrative ---',
            data: null,
            isSeparator: true
          });
          
          // Filtrer les utilisateurs OPA qui ne sont pas déjà dans les intervenants du rapport
          const reportIntervenantIds = reportDetails.intervenants.map(i => i.userId).filter(Boolean);
          const opaUsers = allUsers.filter(user => user.opa === true && !reportIntervenantIds.includes(user._id));
          
          // Ajouter les utilisateurs OPA
          const opaUsersOptions = opaUsers.map(user => {
            const grade = convertGrade(user.rank || '');
            const displayText = `${user.lastname.toUpperCase()} ${user.firstname}, ${grade}`;
            return {
              value: displayText,
              data: {
                isExternal: false,
                premier: false,
                sign: false,
                isOPJ: !!user.opj,
                isOPA: true,
                rank: grade,
                lastname: user.lastname,
                firstname: user.firstname,
                userId: user._id
              },
              isOPAUser: true
            };
          }).sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
          
          allOPAOptions.push(...opaUsersOptions);
        }
        
        // Mettre à jour les valeurs du rapport pour inclure toutes les données OPA
        setFormValues(prev => ({
          ...prev,
          _opaData: allOPAOptions.reduce((acc, option) => {
            if (!option.isSeparator) {
              acc[option.value] = option;
            }
            return acc;
          }, {})
        }));
        
        // Retourner uniquement les valeurs d'affichage pour le select
        return { ...field, options: allOPAOptions.map(option => option.value) };

      case 'personnePhysique': {
        // Stocker les données complètes des personnes physiques (y compris les inconnues)
        const personnesList = Array.isArray(reportDetails.personnesPhysiques)
          ? reportDetails.personnesPhysiques
          : [];
        const unknownCount = personnesList.filter(p => p.isUnknown).length;
        let unknownIndex = 0;

        const personnesOptions = personnesList
          .filter(p => p.lastname || p.isUnknown) // Inclure également les suspects/personnes inconnus
          .map(p => {
            const displayText = p.isUnknown
              ? `Inconnu${unknownCount > 1 ? ` #${++unknownIndex}` : ''}`
              : `${(p.lastname || '').toUpperCase()} ${p.firstname || ''}`.trim();
            return {
              value: displayText || 'Inconnu',
              gender: p.gender || '', // Stocker le gender pour chaque personne
              data: p // Stocker l'objet complet pour un accès ultérieur
            };
          })
          .sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
        
        // Mettre à jour les valeurs du rapport pour inclure les données des personnes
        setFormValues(prev => ({
          ...prev,
          _personnesData: personnesOptions.reduce((acc, p) => {
            acc[p.value] = p;
            return acc;
          }, {})
        }));
        
        // Retourner uniquement les valeurs d'affichage pour le select
        return { ...field, options: personnesOptions.map(p => p.value) };
      }
        
      case 'objet':
        // Stocker les données complètes des objets
        const objetsOptions = reportDetails.objets.map(o => {
          // Afficher le numéro SIN entre parenthèses si renseigné
          const displayText = o.sinNumber ? `${o.description} (SIN ${o.sinNumber})` : o.description;
          return {
            value: displayText,
            data: o // Stocker l'objet complet pour un accès ultérieur
          };
        })
        .sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
        
        // Mettre à jour les valeurs du rapport pour inclure les données des objets
        setFormValues(prev => ({
          ...prev,
          _objetsData: objetsOptions.reduce((acc, o) => {
            acc[o.value] = o;
            return acc;
          }, {})
        }));
        
        // Retourner uniquement les valeurs d'affichage pour le select
        return { ...field, options: objetsOptions.map(o => o.value) };
        
      case 'vehicule':
        // Stocker les données complètes des véhicules
        const vehiculesOptions = reportDetails.vehicules.map(v => {
          const parts = [v.brand, v.plateNumber, v.chassisNumber].map(part => part || '');
          const displayText = parts.join(' - ');
          return {
            value: displayText,
            data: v // Stocker l'objet complet pour un accès ultérieur
          };
        })
        .sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
        
        // Mettre à jour les valeurs du rapport pour inclure les données des véhicules
        setFormValues(prev => ({
          ...prev,
          _vehiculesData: vehiculesOptions.reduce((acc, v) => {
            acc[v.value] = v;
            return acc;
          }, {})
        }));
        
        // Retourner uniquement les valeurs d'affichage pour le select
        return { ...field, options: vehiculesOptions.map(v => v.value) };
        
      case 'personneMorale': {
        const personnesMoralesList = Array.isArray(reportDetails.personnesMorales)
          ? reportDetails.personnesMorales
          : [];

        const personnesMoralesOptions = personnesMoralesList
          .filter(pm => pm?.denomination)
          .map(pm => {
            const qualiteSuffix = pm.qualite ? ` (${pm.qualite})` : '';
            const displayText = `${pm.denomination}${qualiteSuffix}`;
            return {
              value: displayText,
              data: pm
            };
          })
          .sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
        
        setFormValues(prev => ({
          ...prev,
          _personnesMoralesData: personnesMoralesOptions.reduce((acc, pm) => {
            acc[pm.value] = pm;
            return acc;
          }, {})
        }));
        
        return { ...field, options: personnesMoralesOptions.map(pm => pm.value) };
      }

      case 'personnesConcernees': {
        const personnesPhysiquesList = Array.isArray(reportDetails.personnesPhysiques)
          ? reportDetails.personnesPhysiques.filter(p => p.lastname || p.isUnknown)
          : [];
        const personnesPhysiquesUnknownCount = personnesPhysiquesList.filter(p => p.isUnknown).length;
        let personnesPhysiquesUnknownIndex = 0;
        const personnesMoralesList = Array.isArray(reportDetails.personnesMorales)
          ? reportDetails.personnesMorales.filter(pm => pm?.denomination)
          : [];

        const personnesPhysiquesOptions = personnesPhysiquesList
          .map(p => {
            const lastname = (p.lastname || '').toUpperCase();
            const firstname = p.firstname ? ` ${p.firstname}` : '';
            const label = p.isUnknown
              ? `Inconnu${personnesPhysiquesUnknownCount > 1 ? ` #${++personnesPhysiquesUnknownIndex}` : ''}`
              : `${lastname}${firstname}`.trim();
            return {
              value: label,
              type: 'physique',
              gender: p.gender || '',
              data: p
            };
          })
          .sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));

        const personnesMoralesOptions = personnesMoralesList
          .map(pm => {
            const qualiteSuffix = pm.qualite ? ` (${pm.qualite})` : '';
            return {
              value: `${pm.denomination}${qualiteSuffix}`,
              type: 'morale',
              data: pm
            };
          })
          .sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));

        const combinedOptions = [];
        if (personnesPhysiquesOptions.length) {
          combinedOptions.push({
            value: '--- Personnes physiques ---',
            isSeparator: true
          });
          combinedOptions.push(...personnesPhysiquesOptions);
        }
        if (personnesMoralesOptions.length) {
          combinedOptions.push({
            value: '--- Personnes morales ---',
            isSeparator: true
          });
          combinedOptions.push(...personnesMoralesOptions);
        }

        setFormValues(prev => ({
          ...prev,
          _personnesConcerneesData: combinedOptions.reduce((acc, option) => {
            if (!option.isSeparator) {
              acc[option.value] = option;
            }
            return acc;
          }, {})
        }));

        return { ...field, options: combinedOptions.map(option => option.value) };
      }

      case 'entitesPartie': {
        const vehiculesList = Array.isArray(reportDetails.vehicules)
          ? reportDetails.vehicules.filter(v =>
              (v.brand && v.brand.trim() !== '') ||
              (v.plateNumber && v.plateNumber.trim() !== '') ||
              (v.chassisNumber && v.chassisNumber.trim() !== '')
            )
          : [];
        const personnesPhysiquesList = Array.isArray(reportDetails.personnesPhysiques)
          ? reportDetails.personnesPhysiques.filter(p => p.lastname || p.isUnknown)
          : [];
        const personnesPhysiquesUnknownCount = personnesPhysiquesList.filter(p => p.isUnknown).length;
        let personnesPhysiquesUnknownIndex = 0;
        const personnesMoralesList = Array.isArray(reportDetails.personnesMorales)
          ? reportDetails.personnesMorales.filter(pm => pm?.denomination)
          : [];

        const formatVehiculeLabel = (vehicule) => {
          const parts = [vehicule.brand, vehicule.plateNumber, vehicule.chassisNumber]
            .map(part => part || '')
            .filter(Boolean);
          if (parts.length) {
            return parts.join(' - ');
          }
          return vehicule.reference || 'Véhicule';
        };

        const vehiculesOptions = vehiculesList
          .map(v => ({
            value: formatVehiculeLabel(v),
            type: 'vehicule',
            data: v
          }))
          .sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));

        const personnesPhysiquesOptions = personnesPhysiquesList
          .map(p => {
            const lastname = (p.lastname || '').toUpperCase();
            const firstname = p.firstname ? ` ${p.firstname}` : '';
            const label = p.isUnknown
              ? `Inconnu${personnesPhysiquesUnknownCount > 1 ? ` #${++personnesPhysiquesUnknownIndex}` : ''}`
              : `${lastname}${firstname}`.trim();
            return {
              value: label,
              type: 'personnePhysique',
              gender: p.gender || '',
              data: p
            };
          })
          .sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));

        const personnesMoralesOptions = personnesMoralesList
          .map(pm => {
            const qualiteSuffix = pm.qualite ? ` (${pm.qualite})` : '';
            return {
              value: `${pm.denomination}${qualiteSuffix}`,
              type: 'personneMorale',
              data: pm
            };
          })
          .sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));

        const combinedOptions = [];
        if (vehiculesOptions.length) {
          combinedOptions.push({
            value: '--- Véhicules ---',
            isSeparator: true
          });
          combinedOptions.push(...vehiculesOptions);
        }
        if (personnesPhysiquesOptions.length) {
          combinedOptions.push({
            value: '--- Personnes physiques ---',
            isSeparator: true
          });
          combinedOptions.push(...personnesPhysiquesOptions);
        }
        if (personnesMoralesOptions.length) {
          combinedOptions.push({
            value: '--- Personnes morales ---',
            isSeparator: true
          });
          combinedOptions.push(...personnesMoralesOptions);
        }

        setFormValues(prev => ({
          ...prev,
          _entitesPartieData: combinedOptions.reduce((acc, option) => {
            if (!option.isSeparator) {
              acc[option.value] = option;
            }
            return acc;
          }, {})
        }));

        return { ...field, options: combinedOptions.map(option => option.value) };
      }
        
      case 'fait':
        // Stocker les données complètes des faits
        const faitsOptions = reportDetails.faits.map(f => {
          let displayText = '';
          if (f.isUndetermined) {
            displayText = 'Fait non déterminé';
          } else {
            displayText = f.fait || '';
            
            // Ajouter la date
            if (f.timeType === 'fixed' && f.fixedDateTime) {
              const date = new Date(f.fixedDateTime);
              const dateStr = date.toLocaleDateString('fr-FR');
              displayText += ` - ${dateStr}`;
            } else if (f.timeType === 'range' && f.rangeLowDateTime) {
              const date = new Date(f.rangeLowDateTime);
              const dateStr = date.toLocaleDateString('fr-FR');
              displayText += ` - du ${dateStr}`;
            }
            
            // Ajouter le lieu
            if (f.address) {
              displayText += ` à ${f.address}`;
            }
          }
          
          return {
            value: displayText,
            data: f
          };
        })
        .sort((a, b) => a.value.localeCompare(b.value, 'fr', { sensitivity: 'accent' }));
        
        setFormValues(prev => ({
          ...prev,
          _faitsData: faitsOptions.reduce((acc, f) => {
            acc[f.value] = f;
            return acc;
          }, {})
        }));
        
        return { ...field, options: faitsOptions.map(f => f.value) };
        
      case 'reportType':
        return { ...field, type: 'select', options: [reportDetails.reportType] };
      case 'indice':
        return { ...field, type: 'select', options: [reportDetails.indice] };
      case 'number':
        return { ...field, type: 'select', options: [reportDetails.number] };
      case 'year':
        return { ...field, type: 'select', options: [reportDetails.year] };
      case 'unit':
        return { ...field, type: 'select', options: [reportDetails.unit] };
      default:
        return field;
    }
  };
  
  // Gestion des changements dans le formulaire
  const handleInputChange = (e, field) => {
    const { name, value, checked, type } = e.target;
    const isSelectField = field?.type === 'select';
    const isMultipleSelect = isSelectField && field?.multiple;
    const normalizedValue = isMultipleSelect ? (Array.isArray(value) ? value : (value ? [value] : [])) : value;

    // Marquer le champ comme modifié
    setModifiedFields(prev => new Set([...prev, name]));

    if (isSelectField && isMultipleSelect) {
      setFormValues(prev => ({
        ...prev,
        [name]: normalizedValue
      }));
      return;
    }

    // Pour les champs de type select avec des données associées
    if (type !== 'checkbox') {
      // Vérifier s'il s'agit d'un champ avec des données associées
      const baseType = name.replace(/[0-9]+$/, '');
      let dataKey = null;
      let dataObject = null;
      
      // Déterminer le type de données associées
      if (baseType === 'personnePhysique' && formValues._personnesData && formValues._personnesData[normalizedValue]) {
        dataKey = '_personnesData';
        dataObject = formValues._personnesData[normalizedValue];
      } else if (baseType === 'intervenant' && formValues._intervenantsData && formValues._intervenantsData[normalizedValue]) {
        dataKey = '_intervenantsData';
        dataObject = formValues._intervenantsData[normalizedValue];
      } else if (baseType === 'opj' && formValues._opjData && formValues._opjData[normalizedValue]) {
        dataKey = '_opjData';
        dataObject = formValues._opjData[normalizedValue];
      } else if (baseType === 'opa' && formValues._opaData && formValues._opaData[normalizedValue]) {
        dataKey = '_opaData';
        dataObject = formValues._opaData[normalizedValue];
      } else if (baseType === 'objet' && formValues._objetsData && formValues._objetsData[normalizedValue]) {
        dataKey = '_objetsData';
        dataObject = formValues._objetsData[normalizedValue];
      } else if (baseType === 'vehicule' && formValues._vehiculesData && formValues._vehiculesData[normalizedValue]) {
        dataKey = '_vehiculesData';
        dataObject = formValues._vehiculesData[normalizedValue];
      } else if (baseType === 'personneMorale' && formValues._personnesMoralesData && formValues._personnesMoralesData[normalizedValue]) {
        dataKey = '_personnesMoralesData';
        dataObject = formValues._personnesMoralesData[normalizedValue];
      } else if (baseType === 'fait' && formValues._faitsData && formValues._faitsData[normalizedValue]) {
        dataKey = '_faitsData';
        dataObject = formValues._faitsData[normalizedValue];
      }
      
      // Si des données associées ont été trouvées
      if (dataObject) {
        const updatedValues = {
          ...formValues,
          [name]: normalizedValue,
        };
        
        // Ajouter les données spécifiques selon le type
        if (dataKey === '_personnesData') {
          updatedValues[`${name}_gender`] = dataObject.gender || '';
          updatedValues[`${name}_data`] = dataObject.data;
        } else if (dataKey === '_opjData' || dataKey === '_opaData') {
          updatedValues[`${name}_data`] = dataObject.data;
        } else {
          updatedValues[`${name}_data`] = dataObject.data;
        }
        
        setFormValues(updatedValues);
        return;
      }
    }
    
    // Comportement par défaut pour les autres champs
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : normalizedValue
    }));
  };
  
  // Gestion des changements d'éditeur Quill
  const handleEditorChange = (value, name) => {
    // Marquer le champ comme modifié
    setModifiedFields(prev => new Set([...prev, name]));

    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Gestion des champs insert
  const handleInsertChange = (e, name) => {
    setInsertValues(prev => ({
      ...prev,
      [name]: e.target.value
    }));
  };
  
  const handleAddItem = (name) => {
    if (!insertValues[name] || insertValues[name].trim() === '') return;

    // Marquer le champ comme modifié
    setModifiedFields(prev => new Set([...prev, name]));

    setFormValues(prev => ({
      ...prev,
      [name]: [...(prev[name] || []), insertValues[name]]
    }));

    setInsertValues(prev => ({
      ...prev,
      [name]: ''
    }));
  };
  
  const handleRemoveItem = (name, index) => {
    // Marquer le champ comme modifié
    setModifiedFields(prev => new Set([...prev, name]));

    setFormValues(prev => ({
      ...prev,
      [name]: prev[name].filter((_, i) => i !== index)
    }));
  };
  
  const handleMoveItemUp = (name, index) => {
    if (index === 0) return; // Déjà en haut

    // Marquer le champ comme modifié
    setModifiedFields(prev => new Set([...prev, name]));

    setFormValues(prev => {
      const items = [...prev[name]];
      const temp = items[index];
      items[index] = items[index - 1];
      items[index - 1] = temp;

      return {
        ...prev,
        [name]: items
      };
    });
  };
  
  const handleMoveItemDown = (name, index) => {
    // Marquer le champ comme modifié
    setModifiedFields(prev => new Set([...prev, name]));

    setFormValues(prev => {
      const items = prev[name] || [];
      if (index >= items.length - 1) return prev; // Déjà en bas

      const newItems = [...items];
      const temp = newItems[index];
      newItems[index] = newItems[index + 1];
      newItems[index + 1] = temp;

      return {
        ...prev,
        [name]: newItems
      };
    });
  };
  
  const handleMoveMultipleItemUp = (name, index) => {
    if (index === 0) return; // Déjà en haut

    // Marquer le champ comme modifié
    setModifiedFields(prev => new Set([...prev, name]));

    setFormValues(prev => {
      const items = [...(prev[name] || [])];
      const temp = items[index];
      items[index] = items[index - 1];
      items[index - 1] = temp;

      return {
        ...prev,
        [name]: items
      };
    });
  };
  
  const handleMoveMultipleItemDown = (name, index) => {
    // Marquer le champ comme modifié
    setModifiedFields(prev => new Set([...prev, name]));

    setFormValues(prev => {
      const items = prev[name] || [];
      if (index >= items.length - 1) return prev; // Déjà en bas

      const newItems = [...items];
      const temp = newItems[index];
      newItems[index] = newItems[index + 1];
      newItems[index + 1] = temp;

      return {
        ...prev,
        [name]: newItems
      };
    });
  };
  
  const handleMoveNestedItemUp = (parentName, parentIndex, childName, childIndex) => {
    if (childIndex === 0) return; // Déjà en haut

    // Marquer le champ parent comme modifié
    setModifiedFields(prev => new Set([...prev, parentName]));

    setFormValues(prev => {
      const newItems = [...(prev[parentName] || [])];
      const subItems = [...(newItems[parentIndex][childName] || [])];
      const temp = subItems[childIndex];
      subItems[childIndex] = subItems[childIndex - 1];
      subItems[childIndex - 1] = temp;

      newItems[parentIndex] = {
        ...newItems[parentIndex],
        [childName]: subItems
      };

      return {
        ...prev,
        [parentName]: newItems
      };
    });
  };
  
  const handleMoveNestedItemDown = (parentName, parentIndex, childName, childIndex) => {
    // Marquer le champ parent comme modifié
    setModifiedFields(prev => new Set([...prev, parentName]));

    setFormValues(prev => {
      const newItems = [...(prev[parentName] || [])];
      const subItems = newItems[parentIndex][childName] || [];
      if (childIndex >= subItems.length - 1) return prev; // Déjà en bas

      const updatedSubItems = [...subItems];
      const temp = updatedSubItems[childIndex];
      updatedSubItems[childIndex] = updatedSubItems[childIndex + 1];
      updatedSubItems[childIndex + 1] = temp;

      newItems[parentIndex] = {
        ...newItems[parentIndex],
        [childName]: updatedSubItems
      };

      return {
        ...prev,
        [parentName]: newItems
      };
    });
  };
  
  // Fonction helper pour évaluer une règle (simple ou imbriquée)
  const evaluateRule = (rule) => {
    // Si la règle a un opérateur (AND/OR) ET un tableau de règles, c'est une règle imbriquée
    if (rule.rules && (rule.operator === 'AND' || rule.operator === 'OR')) {
      const { operator, rules } = rule;
      
      if (operator === 'AND') {
        return rules.every(subRule => evaluateRule(subRule));
      }
      
      if (operator === 'OR') {
        return rules.some(subRule => evaluateRule(subRule));
      }
    }
    
    // Sinon, c'est une règle simple
    const { field: conditionField, value: conditionValue, operator: ruleOperator = 'eq' } = rule;
    
    switch (ruleOperator) {
      case 'eq': return formValues[conditionField] === conditionValue;
      case 'neq': return formValues[conditionField] !== conditionValue;
      case 'gt': return formValues[conditionField] > conditionValue;
      case 'lt': return formValues[conditionField] < conditionValue;
      case 'contains': return formValues[conditionField]?.includes(conditionValue);
      default: return formValues[conditionField] === conditionValue;
    }
  };

  // Vérifier si un champ doit être affiché en fonction de sa condition
  const shouldShowField = (field) => {
    if (!field.condition) return true;
    
    // Gestion des conditions avancées
    if (field.condition.operator && field.condition.rules) {
      return evaluateRule(field.condition);
    }
    
    // Condition simple (rétrocompatibilité)
    const { field: conditionField, value: conditionValue, operator: conditionOperator = 'eq' } = field.condition;
    
    switch (conditionOperator) {
      case 'eq': 
        return formValues[conditionField] === conditionValue;
      case 'neq': 
        return formValues[conditionField] !== conditionValue;
      case 'gt': 
        return formValues[conditionField] > conditionValue;
      case 'lt': 
        return formValues[conditionField] < conditionValue;
      case 'contains': 
        return formValues[conditionField]?.includes(conditionValue);
      default: 
        return formValues[conditionField] === conditionValue;
    }
  };
  
  // Fonction pour vérifier les conditions dans un élément de multipleItems
  const shouldShowMultiItemField = (subField, item) => {
    if (!subField.condition) return true;
    
    // Helper pour obtenir la valeur d'un champ (d'abord dans item, sinon dans formValues parent)
    const getFieldValue = (fieldName) => {
      // Si le champ existe dans l'item, l'utiliser (priorité aux champs locaux)
      if (item.hasOwnProperty(fieldName)) {
        return item[fieldName];
      }
      // Sinon, chercher dans le formulaire parent
      return formValues[fieldName];
    };
    
    // Fonction helper pour évaluer une règle (simple ou imbriquée)
    const evaluateMultiItemRule = (rule) => {
      // Si la règle a un opérateur (AND/OR) ET un tableau de règles, c'est une règle imbriquée
      if (rule.rules && (rule.operator === 'AND' || rule.operator === 'OR')) {
        const { operator, rules } = rule;
        
        if (operator === 'AND') {
          return rules.every(subRule => evaluateMultiItemRule(subRule));
        }
        
        if (operator === 'OR') {
          return rules.some(subRule => evaluateMultiItemRule(subRule));
        }
      }
      
      // Sinon, c'est une règle simple
      const { field: conditionField, value: conditionValue, operator: ruleOperator = 'eq' } = rule;
      const fieldValue = getFieldValue(conditionField);
      
      switch (ruleOperator) {
        case 'eq': return fieldValue === conditionValue;
        case 'neq': return fieldValue !== conditionValue;
        case 'gt': return fieldValue > conditionValue;
        case 'lt': return fieldValue < conditionValue;
        case 'contains': return fieldValue?.includes(conditionValue);
        default: return fieldValue === conditionValue;
      }
    };
    
    // Gestion des conditions avancées
    if (subField.condition.operator && subField.condition.rules) {
      return evaluateMultiItemRule(subField.condition);
    }
    
    // Condition simple (rétrocompatibilité)
    const { field: conditionField, value: conditionValue, operator: conditionOperator = 'eq' } = subField.condition;
    const fieldValue = getFieldValue(conditionField);
    
    switch (conditionOperator) {
      case 'eq': 
        return fieldValue === conditionValue;
      case 'neq': 
        return fieldValue !== conditionValue;
      case 'gt': 
        return fieldValue > conditionValue;
      case 'lt': 
        return fieldValue < conditionValue;
      case 'contains': 
        return fieldValue?.includes(conditionValue);
      default: 
        return fieldValue === conditionValue;
    }
  };
  
  // Déterminer la taille de la grille en fonction des propriétés du champ
  const getGridSize = (field) => {
    if (field.fullWidth === true) return 12;
    return field.type === 'textarea' || field.type === 'editor' || field.type === 'insert' ? 12 : 6;
  };

const resolveFieldSize = (field, defaultSize = 'medium') =>
  field?.size === 'small' ? 'small' : defaultSize;
  
  // Calculer la progression du formulaire
  const calculateProgress = () => {
    let totalFields = 0;
    let filledFields = 0;
    
    const isFieldFilled = (field, value) => {
      switch (field.type) {
        case 'checkbox':
          // Les checkbox ne sont pas comptés dans la progression car elles ont toujours une valeur
          return { counted: false, filled: false };
        case 'multipleItems':
          // Vérifier s'il y a au moins un élément et que le champ a été modifié
          const hasItems = Array.isArray(value) && value.length > 0;
          const isMultipleModified = modifiedFields.has(field.name);
          return { counted: true, filled: hasItems && isMultipleModified };
        case 'insert':
          // Vérifier s'il y a au moins un élément et que le champ a été modifié
          const hasInsertItems = Array.isArray(value) && value.length > 0;
          const isInsertModified = modifiedFields.has(field.name);
          return { counted: true, filled: hasInsertItems && isInsertModified };
        case 'editor':
          // Vérifier si le contenu HTML n'est pas vide (en excluant les balises vides) et que le champ a été modifié
          const textContent = value ? value.replace(/<[^>]*>/g, '').trim() : '';
          const isEditorModified = modifiedFields.has(field.name);
          return { counted: true, filled: textContent.length > 0 && isEditorModified };
        case 'date':
        case 'datetime':
        case 'monthyear':
        case 'time':
        case 'select':
          // Pour les champs select, une valeur (ou une sélection) suffit même si c'est une defaultValue
          const hasSelectValue = Array.isArray(value)
            ? value.length > 0
            : value !== undefined && value !== null && value !== '';
          return { counted: true, filled: hasSelectValue };
        default:
          // Pour les autres types (text, select, etc.)
          // Ne considérer comme rempli que si le champ a été modifié ET a une valeur non vide
          const isDefaultModified = modifiedFields.has(field.name);
          const hasValue = Array.isArray(value)
            ? value.length > 0
            : value !== undefined && value !== null && value !== '';
          return { counted: true, filled: hasValue && isDefaultModified };
      }
    };
    
    const processField = (field) => {
      // Ne pas compter les champs non affichés
      if (!shouldShowField(field)) return;
      
      // Ne pas compter les champs optionnels
      if (field.optional === true) return;
      
      // Ne pas compter les types structurels
      if (field.type === 'label' || field.divider) return;
      
      // Traiter les groupes et sections
      if (field.type === 'group' || field.type === 'section') {
        if (field.fields && Array.isArray(field.fields)) {
          field.fields.forEach(subField => {
            const fullName = `${field.name}.${subField.name}`;
            processField({ ...subField, name: fullName });
          });
        }
        return;
      }
      
      // Traiter les multipleItems
      if (field.type === 'multipleItems') {
        const result = isFieldFilled(field, formValues[field.name]);
        if (result.counted) {
          totalFields++;
          if (result.filled) filledFields++;
        }
        return;
      }
      
      // Traiter les champs normaux (tous les champs visibles sont comptés)
      const result = isFieldFilled(field, formValues[field.name]);
      if (result.counted) {
        totalFields++;
        if (result.filled) filledFields++;
      }
    };
    
    // Parcourir tous les champs du formulaire
    formFields.forEach(field => processField(field));
    
    // Calculer le pourcentage
    const percentage = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
    
    return {
      total: totalFields,
      filled: filledFields,
      percentage: percentage
    };
  };
  
  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    const validateField = (field, fieldName = field.name) => {
      // Ne valider que les champs réellement affichés
      if (!shouldShowField(field)) {
        return;
      }
      
      const fieldValue = formValues[fieldName];
      
      // Validation pour les champs normaux
      const isEmptyValue = Array.isArray(fieldValue)
        ? fieldValue.length === 0
        : fieldValue === undefined || fieldValue === null || fieldValue === '';
      
      if (field.required && field.type !== 'multipleItems' && isEmptyValue) {
        errors[fieldName] = `Le champ "${field.label}" est obligatoire`;
        isValid = false;
      }
      
      // Validation pour les champs multipleItems
      if (field.type === 'multipleItems' && field.required) {
        if (!formValues[fieldName] || !Array.isArray(formValues[fieldName]) || formValues[fieldName].length === 0) {
          errors[fieldName] = `Vous devez ajouter au moins un élément dans "${field.label}"`;
          isValid = false;
        } else {
          // Vérifier que chaque élément a ses champs required remplis
          formValues[fieldName].forEach((item, index) => {
            field.fields.forEach(subField => {
              // Vérifier si le sous-champ doit être affiché dans cet élément
              if (shouldShowMultiItemField(subField, item)) {
                if (subField.required && (!item[subField.name] || item[subField.name] === '')) {
                  errors[fieldName] = `L'élément #${index + 1} a des champs obligatoires non remplis`;
                  isValid = false;
                }
              }
            });
          });
        }
      }
      
      // Validation pour les groupes et sections
      if ((field.type === 'group' || field.type === 'section') && field.fields) {
        field.fields.forEach(subField => {
          const fullFieldName = `${field.name}.${subField.name}`;
          validateField({...subField, name: fullFieldName}, fullFieldName);
        });
      }
    };
    
    formFields.forEach(field => validateField(field));
    
    setFormErrors(errors);
    return isValid;
  };
  
  // Gestion de l'aperçu du formulaire
  const handlePreview = async () => {
    if (!rubric || !rubric.version) {
      setActionError("Version de la rubrique non spécifiée");
      return;
    }
    
    try {
      setPreviewLoading(true);
      setActionError(null);
      setActionErrorDetails(null);
      const token = sessionStorage.getItem('authToken');
      
      // Préparer les valeurs du formulaire avec les données du rapport
      const enhancedFormValues = {
        ...formValues,
        number: reportNumber,
        year: reportYear
      };
      
      // Appeler l'API pour générer le contenu
      const response = await axios.post(
        `/api/rubric/generate/${rubric.version}`,
        enhancedFormValues,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Stocker l'aperçu
      setPreviewContent({
        title: response.data.title,
        content: response.data.content
      });
      
      setPreviewLoading(false);
    } catch (error) {
      console.error("Erreur lors de la génération de la prévisualisation:", error);
      
      // Déterminer le message d'erreur approprié
      let errorMessage = "Erreur inconnue";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message === 'Network Error') {
        errorMessage = "Problème de connexion réseau. Vérifiez votre connexion internet.";
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = "La requête a pris trop de temps. Le serveur est peut-être surchargé.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setActionError("Impossible de générer la prévisualisation.");
      setActionErrorDetails(errorMessage);
      setPreviewLoading(false);
    }
  };
  
  // Gestion de la réinitialisation du formulaire
  const handleReset = () => {
    setResetDialogOpen(true);
  };
  
  // Confirmer la réinitialisation
  const confirmReset = () => {
    // Réinitialiser les valeurs du formulaire
    const initialValues = {};
    const initialInsertValues = {};

    rawFormFields.forEach(field => {
      if (field.type === 'checkbox') {
        initialValues[field.name] = field.defaultValue !== undefined ? field.defaultValue : false;
      } else if (field.type === 'insert') {
        initialValues[field.name] = field.defaultValue || [];
        initialInsertValues[field.name] = '';
      } else {
        initialValues[field.name] = field.defaultValue !== undefined ? field.defaultValue : '';
      }
    });

    setFormValues(initialValues);
    setInsertValues(initialInsertValues);
    setFormErrors({});
    setPreviewContent(null);
    setActionError(null);
    setActionErrorDetails(null);
    setModifiedFields(new Set()); // Réinitialiser les champs modifiés
    clearDraft();
    setResetDialogOpen(false);
  };
  
  // Annuler la réinitialisation
  const cancelReset = () => {
    setResetDialogOpen(false);
  };

  // Gestion de la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!rubric || !rubric.version) {
      setActionError("Version de la rubrique non spécifiée");
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setGeneratingContent(true);
      setActionError(null);
      setActionErrorDetails(null);
      const token = sessionStorage.getItem('authToken');
      
      // Préparer les valeurs du formulaire avec les données du rapport
      const enhancedFormValues = {
        ...formValues,
        number: reportNumber,
        year: reportYear
      };
      
      // Appeler l'API pour générer le contenu
      const response = await axios.post(
        `/api/rubric/generate/${rubric.version}`,
        enhancedFormValues,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Si onSave est fourni, appeler avec le contenu généré
      if (onSave && typeof onSave === 'function') {
        onSave({
          title: response.data.title,
          content: response.data.content,
          generated: true
        });
      }

      // Log de la génération de rubrique
      const generatedText = (response.data.content || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      logAction(LOG_ACTIONS.RUBRIC_GENERATE, formatReportNumber(reportNumber, reportYear), {
        rubricTitle: response.data.title,
        version: rubric.version,
        contenuGenere: generatedText.substring(0, 500)
      });

      clearDraft();
      
      // Enregistrer les statistiques de génération
      try {
        const generatedContent = response.data.content || '';
        // Compter les mots générés (retirer les balises HTML)
        const textContent = generatedContent.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        const wordCount = textContent ? textContent.split(' ').length : 0;
        
        // Enregistrer la rubrique générée
        await axios.post(
          '/api/stats',
          { type: 'rubric_generated', value: 1 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Enregistrer le nombre de mots générés
        if (wordCount > 0) {
          await axios.post(
            '/api/stats',
            { type: 'words_generated', value: wordCount },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      } catch (statsError) {
        // Ne pas bloquer si l'enregistrement des stats échoue
        console.warn('Échec enregistrement statistiques:', statsError);
      }
      
      setGeneratingContent(false);
    } catch (error) {
      console.error("Erreur lors de la génération du contenu:", error);
      
      // Déterminer le message d'erreur approprié
      let errorMessage = "Erreur inconnue";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message === 'Network Error') {
        errorMessage = "Problème de connexion réseau. Vérifiez votre connexion internet.";
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = "La requête a pris trop de temps. Le serveur est peut-être surchargé.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setActionError("Impossible de générer le contenu.");
      setActionErrorDetails(errorMessage);
      setGeneratingContent(false);
    }
  };
  
  // Rendu des champs du formulaire
  const renderFormField = (field) => {
    if (!shouldShowField(field)) return null;
    
    const hasError = !!formErrors[field.name];
    
    // Appliquer des styles personnalisés
    const customStyle = field.style || {};
  const fieldSize = resolveFieldSize(field);
    
    // Si c'est un séparateur, on renvoie une ligne horizontale
    if (field.divider) {
      return (
        <Grid item xs={12} key={`divider-${field.name}`}>
          <Divider sx={{ my: 2 }} />
        </Grid>
      );
    }

    // Si c'est un groupe de champs
    if (field.type === 'group') {
      // Vérifier s'il y a des champs visibles dans le groupe
      const visibleFields = field.fields?.filter(subField => 
        shouldShowField({...subField, name: `${field.name}.${subField.name}`})
      ) || [];
      
      // Si aucun champ n'est visible, ne pas afficher le groupe
      if (visibleFields.length === 0) {
        return null;
      }
      
      return (
        <Box
          key={field.name}
          sx={{
            width: '100%',
            p: 2,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            mb: 2,
            position: 'relative',
            ...customStyle
          }}
        >
          {field.title && (
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              {field.title}
            </Typography>
          )}
          <Grid container spacing={1}>
            {field.fields?.map(subField => {
              // Vérifier si le sous-champ doit être affiché
              if (!shouldShowField({...subField, name: `${field.name}.${subField.name}`})) {
                return null; // Ne pas rendre du tout l'élément Grid
              }
              
              const gridSize = subField.fullWidth ? 12 : (subField.type === 'textarea' || subField.type === 'editor' || subField.type === 'insert' ? 12 : 6);
              return (
                <Grid item xs={gridSize} key={subField.name}>
                  {renderFormField({...subField, name: `${field.name}.${subField.name}`})}
                </Grid>
              );
            })}
          </Grid>
        </Box>
      );
    }
    
    // Si c'est une section pliable (accordéon)
    if (field.type === 'section') {
      // Vérifier s'il y a des champs visibles dans la section
      const visibleFields = field.fields?.filter(subField => 
        shouldShowField({...subField, name: `${field.name}.${subField.name}`})
      ) || [];
      
      // Si aucun champ n'est visible, ne pas afficher la section
      if (visibleFields.length === 0) {
        return null;
      }
      
      return (
        <Accordion
          key={field.name}
          defaultExpanded={field.defaultOpen !== false}
          sx={{ width: '100%', mb: 2, position: 'relative', ...customStyle }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              {field.title || 'Section'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              {field.fields?.map(subField => {
                // Vérifier si le sous-champ doit être affiché
                if (!shouldShowField({...subField, name: `${field.name}.${subField.name}`})) {
                  return null; // Ne pas rendre du tout l'élément Grid
                }
                
                const gridSize = subField.fullWidth ? 12 : (subField.type === 'textarea' || subField.type === 'editor' || subField.type === 'insert' ? 12 : 6);
                return (
                  <Grid item xs={gridSize} key={subField.name}>
                    {renderFormField({...subField, name: `${field.name}.${subField.name}`})}
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>
      );
    }

    switch (field.type) {
      case 'multipleItems':
        return (
          <Box key={field.name} sx={{ width: '100%', mb: 1, position: 'relative' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <Typography variant="subtitle1" mb={0.5}>
              {field.label} {field.required && <span style={{color: 'red'}}>*</span>}
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Button 
                variant="outlined" 
                startIcon={<AddCircleOutlineIcon />} 
                onClick={() => {
                  // Marquer le champ comme modifié
                  setModifiedFields(prev => new Set([...prev, field.name]));
                  const newItems = [...(formValues[field.name] || []), {}];
                  setFormValues(prev => ({
                    ...prev,
                    [field.name]: newItems
                  }));
                }}
              >
                {field.buttonText || 'Ajouter un élément'}
              </Button>
            </Box>
            
            {Array.isArray(formValues[field.name]) && formValues[field.name].length > 0 ? (
              formValues[field.name].map((item, index) => (
                <Paper key={index} elevation={1} sx={{ p: 1.5, pt: 5, mb: 1, position: 'relative', backgroundColor: isDark ? '#1e3a5f' : '#e3f2fd' }}>
                  <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, zIndex: 1 }}>
                    <IconButton 
                      size="small"
                      onClick={() => handleMoveMultipleItemUp(field.name, index)}
                      disabled={index === 0}
                      title="Monter"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small"
                      onClick={() => handleMoveMultipleItemDown(field.name, index)}
                      disabled={index === formValues[field.name].length - 1}
                      title="Descendre"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small"
                      onClick={() => {
                        // Marquer le champ comme modifié
                        setModifiedFields(prev => new Set([...prev, field.name]));
                        const newItems = formValues[field.name].filter((_, i) => i !== index);
                        setFormValues(prev => ({
                          ...prev,
                          [field.name]: newItems
                        }));
                      }}
                      title="Supprimer"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Grid container spacing={1}>
                    {field.fields.map(subField => {
                      // Vérifier si ce champ spécifique doit être affiché
                      const shouldShow = shouldShowMultiItemField(subField, item);
                      if (!shouldShow) return null;
              const subFieldSize = resolveFieldSize(subField);
                      
                      return (
                        <Grid item xs={subField.fullWidth ? 12 : 6} key={`${field.name}.${index}.${subField.name}`}>
                          <Box sx={{ position: 'relative', width: '100%' }}>
                            {subField.optional && <OptionalBadge isDark={isDark} />}
                            {subField.type === 'insert' ? (
                              <TextField
                                fullWidth
                                label={subField.label}
                                value={formValues[field.name][index][subField.name] || subField.defaultValue || ''}
                                onChange={(e) => {
                                  // Marquer le champ parent comme modifié
                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                  const newItems = [...formValues[field.name]];
                                  newItems[index] = {
                                    ...newItems[index],
                                    [subField.name]: e.target.value
                                  };
                                  setFormValues(prev => ({
                                    ...prev,
                                    [field.name]: newItems
                                  }));
                                }}
                                placeholder={subField.placeholder || ''}
                                margin="dense"
                                size={subFieldSize}
                                required={subField.required}
                              />
                            ) : subField.type === 'select' && (subField.autocomplete || subField.searchable || subField.freeSolo) ? (
                              <SearchableSelectField
                                field={subField}
                                name={subField.name}
                                value={formValues[field.name][index][subField.name]}
                                hasError={false}
                                helperText={subField.helperText || ''}
                                fieldSize={subFieldSize}
                                onValueChange={(nextValue) => {
                                  // Marquer le champ parent comme modifié
                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                  const newItems = [...formValues[field.name]];
                                  newItems[index] = {
                                    ...newItems[index],
                                    [subField.name]: nextValue
                                  };
                                  setFormValues(prev => ({
                                    ...prev,
                                    [field.name]: newItems
                                  }));
                                }}
                              />
                            ) : subField.type === 'select' ? (
                              <FormControl 
                                fullWidth 
                                margin="dense"
                                size={subFieldSize}
                                required={subField.required}
                              >
                                <InputLabel id={`${field.name}-${index}-${subField.name}-label`}>{subField.label}</InputLabel>
                                <Select
                                  labelId={`${field.name}-${index}-${subField.name}-label`}
                                  size={subFieldSize}
                                  multiple={subField.multiple}
                                  value={
                                    subField.multiple
                                      ? formValues[field.name][index][subField.name] || []
                                      : formValues[field.name][index][subField.name] || ''
                                  }
                                  renderValue={
                                    subField.multiple
                                      ? (selected) => (Array.isArray(selected) ? selected.join(', ') : '')
                                      : undefined
                                  }
                                  onChange={(e) => {
                                    // Marquer le champ parent comme modifié
                                    setModifiedFields(prev => new Set([...prev, field.name]));
                                    const rawValue = e.target.value;
                                    const nextValue = subField.multiple
                                      ? (Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []))
                                      : rawValue;
                                    const newItems = [...formValues[field.name]];
                                    newItems[index] = {
                                      ...newItems[index],
                                      [subField.name]: nextValue
                                    };
                                    setFormValues(prev => ({
                                      ...prev,
                                      [field.name]: newItems
                                    }));
                                  }}
                                  label={subField.label}
                                >
                                  {subField.options.map((option, optIndex) => {
                                    // Vérifier si c'est un séparateur
                                    const isSeparator = isSeparatorOption(option);
                                    
                                    // Vérifier si l'option est sélectionnée
                                    const currentValue = formValues[field.name][index][subField.name];
                                    const isSelected = subField.multiple
                                      ? Array.isArray(currentValue) && currentValue.includes(option)
                                      : currentValue === option;
                                    
                                    return (
                                      <MenuItem 
                                        key={optIndex} 
                                        value={option}
                                        disabled={isSeparator}
                                        sx={isSeparator ? { 
                                          fontWeight: 'bold', 
                                          backgroundColor: '#f5f5f5',
                                          '&:hover': { backgroundColor: '#f5f5f5' },
                                          cursor: 'default'
                                        } : {
                                          '&.Mui-selected': {
                                            backgroundColor: '#1976d2',
                                            color: '#ffffff',
                                            fontWeight: 600,
                                            '&:hover': {
                                              backgroundColor: '#1565c0'
                                            }
                                          }
                                        }}
                                      >
                                        {subField.multiple && !isSeparator && (
                                          <Checkbox
                                            checked={isSelected}
                                            size={subFieldSize === 'small' ? 'small' : 'medium'}
                                            sx={{ mr: 1, py: 0 }}
                                          />
                                        )}
                                        {option}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                                {subField.helperText && (
                                  <FormHelperText>{subField.helperText}</FormHelperText>
                                )}
                              </FormControl>
                            ) : subField.type === 'textarea' ? (
                              <TextField
                                fullWidth
                                label={subField.label}
                                value={formValues[field.name][index][subField.name] || subField.defaultValue || ''}
                                onChange={(e) => {
                                  // Marquer le champ parent comme modifié
                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                  const newItems = [...formValues[field.name]];
                                  newItems[index] = {
                                    ...newItems[index],
                                    [subField.name]: e.target.value
                                  };
                                  setFormValues(prev => ({
                                    ...prev,
                                    [field.name]: newItems
                                  }));
                                }}
                                multiline
                                rows={4}
                                margin="dense"
                                size={subFieldSize}
                                required={subField.required}
                                helperText={subField.helperText || ''}
                              />
                            ) : subField.type === 'checkbox' ? (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={formValues[field.name][index][subField.name] || false}
                                    size={subFieldSize === 'small' ? 'small' : 'medium'}
                                    onChange={(e) => {
                                      // Marquer le champ parent comme modifié
                                      setModifiedFields(prev => new Set([...prev, field.name]));
                                      const newItems = [...formValues[field.name]];
                                      newItems[index] = {
                                        ...newItems[index],
                                        [subField.name]: e.target.checked
                                      };
                                      setFormValues(prev => ({
                                        ...prev,
                                        [field.name]: newItems
                                      }));
                                    }}
                                  />
                                }
                                label={subField.label}
                                sx={{ mt: 1 }}
                              />
                            ) : subField.type === 'text' ? (
                              <TextField
                                fullWidth
                                label={subField.label}
                                value={formValues[field.name][index][subField.name] || subField.defaultValue || ''}
                                onChange={(e) => {
                                  // Marquer le champ parent comme modifié
                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                  const newItems = [...formValues[field.name]];
                                  newItems[index] = {
                                    ...newItems[index],
                                    [subField.name]: e.target.value
                                  };
                                  setFormValues(prev => ({
                                    ...prev,
                                    [field.name]: newItems
                                  }));
                                }}
                                margin="dense"
                                size={subFieldSize}
                                required={subField.required}
                                placeholder={subField.placeholder || ''}
                                helperText={subField.helperText || ''}
                              />
                            ) : subField.type === 'date' ? (
                              <TextField
                                fullWidth
                                label={subField.label}
                                type="date"
                                value={formValues[field.name][index][subField.name] || subField.defaultValue || ''}
                                onChange={(e) => {
                                  // Marquer le champ parent comme modifié
                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                  const newItems = [...formValues[field.name]];
                                  newItems[index] = {
                                    ...newItems[index],
                                    [subField.name]: e.target.value
                                  };
                                  setFormValues(prev => ({
                                    ...prev,
                                    [field.name]: newItems
                                  }));
                                }}
                                margin="dense"
                                size={subFieldSize}
                                required={subField.required}
                                InputLabelProps={{ shrink: true }}
                                helperText={subField.helperText || ''}
                                sx={{
                                  '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                    filter: isDark ? 'invert(1)' : 'none',
                                    cursor: 'pointer'
                                  }
                                }}
                              />
                            ) : subField.type === 'datetime' ? (
                              <TextField
                                fullWidth
                                label={subField.label}
                                type="datetime-local"
                                value={formValues[field.name][index][subField.name] || subField.defaultValue || ''}
                                onChange={(e) => {
                                  // Marquer le champ parent comme modifié
                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                  const newItems = [...formValues[field.name]];
                                  newItems[index] = {
                                    ...newItems[index],
                                    [subField.name]: e.target.value
                                  };
                                  setFormValues(prev => ({
                                    ...prev,
                                    [field.name]: newItems
                                  }));
                                }}
                                margin="dense"
                                size={subFieldSize}
                                required={subField.required}
                                InputLabelProps={{ shrink: true }}
                                helperText={subField.helperText || ''}
                                sx={{
                                '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                                  filter: isDark ? 'invert(1)' : 'none',
                                  cursor: 'pointer'
                                }
                              }}
                            />
                          ) : subField.type === 'time' ? (
                            <TextField
                              fullWidth={subField.fullWidth !== false}
                              label={subField.label}
                              type="time"
                              value={formValues[field.name][index][subField.name] || subField.defaultValue || ''}
                              onChange={(e) => {
                                // Marquer le champ parent comme modifié
                                setModifiedFields(prev => new Set([...prev, field.name]));
                                const newItems = [...formValues[field.name]];
                                newItems[index] = {
                                  ...newItems[index],
                                  [subField.name]: e.target.value
                                };
                                setFormValues(prev => ({
                                  ...prev,
                                  [field.name]: newItems
                                }));
                              }}
                              margin="dense"
                              size={subFieldSize}
                              required={subField.required}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{
                                step: 1 // Permet les secondes
                              }}
                              helperText={subField.helperText || ''}
                              sx={{
                                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                                  cursor: 'pointer',
                                  filter: 'invert(0.5)'
                                }
                              }}
                            />
                          ) : subField.type === 'monthyear' ? (
                            <TextField
                              fullWidth
                              label={subField.label}
                              type="month"
                              value={formValues[field.name][index][subField.name] || subField.defaultValue || ''}
                              onChange={(e) => {
                                // Marquer le champ parent comme modifié
                                setModifiedFields(prev => new Set([...prev, field.name]));
                                const newItems = [...formValues[field.name]];
                                newItems[index] = {
                                  ...newItems[index],
                                  [subField.name]: e.target.value
                                };
                                setFormValues(prev => ({
                                  ...prev,
                                  [field.name]: newItems
                                }));
                              }}
                              margin="dense"
                              size={subFieldSize}
                              required={subField.required}
                              InputLabelProps={{ shrink: true }}
                              helperText={subField.helperText || ''}
                              sx={{
                                '& input[type="month"]::-webkit-calendar-picker-indicator': {
                                  filter: isDark ? 'invert(1)' : 'none',
                                  cursor: 'pointer'
                                }
                              }}
                            />
                          ) : subField.type === 'multipleItems' ? (
                            <Box sx={{ width: '100%', mt: 1 }}>
                              <Typography variant="subtitle2" mb={0.5}>
                                {subField.label}
                              </Typography>
                              <Button 
                                variant="outlined" 
                                size="small"
                                startIcon={<AddCircleOutlineIcon />} 
                                onClick={() => {
                                  // Marquer le champ parent comme modifié
                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                  const newItems = [...formValues[field.name]];
                                  const currentSubItems = newItems[index][subField.name] || [];
                                  newItems[index] = {
                                    ...newItems[index],
                                    [subField.name]: [...currentSubItems, {}]
                                  };
                                  setFormValues(prev => ({
                                    ...prev,
                                    [field.name]: newItems
                                  }));
                                }}
                                sx={{ mb: 1 }}
                              >
                                {subField.buttonText || 'Ajouter un élément'}
                              </Button>
                              
                              {Array.isArray(formValues[field.name][index][subField.name]) && 
                               formValues[field.name][index][subField.name].length > 0 ? (
                                formValues[field.name][index][subField.name].map((subItem, subIndex) => (
                                  <Paper key={subIndex} elevation={2} sx={{ p: 2, pt: 5, mb: 1, position: 'relative', backgroundColor: isDark ? '#1a3350' : '#bbdefb' }}>
                                    <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5, zIndex: 1 }}>
                                      <IconButton 
                                        size="small"
                                        onClick={() => handleMoveNestedItemUp(field.name, index, subField.name, subIndex)}
                                        disabled={subIndex === 0}
                                        title="Monter"
                                      >
                                        <ArrowUpwardIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton 
                                        size="small"
                                        onClick={() => handleMoveNestedItemDown(field.name, index, subField.name, subIndex)}
                                        disabled={subIndex === formValues[field.name][index][subField.name].length - 1}
                                        title="Descendre"
                                      >
                                        <ArrowDownwardIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton 
                                        size="small"
                                        onClick={() => {
                                          // Marquer le champ parent comme modifié
                                          setModifiedFields(prev => new Set([...prev, field.name]));
                                          const newItems = [...formValues[field.name]];
                                          const updatedSubItems = newItems[index][subField.name].filter((_, i) => i !== subIndex);
                                          newItems[index] = {
                                            ...newItems[index],
                                            [subField.name]: updatedSubItems
                                          };
                                          setFormValues(prev => ({
                                            ...prev,
                                            [field.name]: newItems
                                          }));
                                        }}
                                        title="Supprimer"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                    
                                    <Grid container spacing={1}>
                                      {subField.fields.map(nestedField => {
                                        const shouldShowNested = shouldShowMultiItemField(nestedField, subItem);
                                        if (!shouldShowNested) return null;
                                        const nestedFieldSize = resolveFieldSize(nestedField, 'small');
                                        
                                        return (
                                          <Grid item xs={nestedField.fullWidth ? 12 : 6} key={`${field.name}.${index}.${subField.name}.${subIndex}.${nestedField.name}`}>
                                            {nestedField.type === 'text' ? (
                                              <TextField
                                                fullWidth
                                                label={nestedField.label}
                                                value={formValues[field.name][index][subField.name][subIndex][nestedField.name] || nestedField.defaultValue || ''}
                                                onChange={(e) => {
                                                  // Marquer le champ parent comme modifié
                                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                                  const newItems = [...formValues[field.name]];
                                                  const updatedSubItems = [...newItems[index][subField.name]];
                                                  updatedSubItems[subIndex] = {
                                                    ...updatedSubItems[subIndex],
                                                    [nestedField.name]: e.target.value
                                                  };
                                                  newItems[index] = {
                                                    ...newItems[index],
                                                    [subField.name]: updatedSubItems
                                                  };
                                                  setFormValues(prev => ({
                                                    ...prev,
                                                    [field.name]: newItems
                                                  }));
                                                }}
                                                margin="dense"
                                                required={nestedField.required}
                                                placeholder={nestedField.placeholder || ''}
                                              size={nestedFieldSize}
                                                helperText={nestedField.helperText || ''}
                                              />
                                            ) : nestedField.type === 'select' ? (
                                              <FormControl 
                                                fullWidth 
                                                margin="dense"
                                                required={nestedField.required}
                                              size={nestedFieldSize}
                                              >
                                                <InputLabel>{nestedField.label}</InputLabel>
                                                <Select
                                                size={nestedFieldSize}
                                                  value={formValues[field.name][index][subField.name][subIndex][nestedField.name] || ''}
                                                  onChange={(e) => {
                                                    const newItems = [...formValues[field.name]];
                                                    const updatedSubItems = [...newItems[index][subField.name]];
                                                    updatedSubItems[subIndex] = {
                                                      ...updatedSubItems[subIndex],
                                                      [nestedField.name]: e.target.value
                                                    };
                                                    newItems[index] = {
                                                      ...newItems[index],
                                                      [subField.name]: updatedSubItems
                                                    };
                                                    setFormValues(prev => ({
                                                      ...prev,
                                                      [field.name]: newItems
                                                    }));
                                                  }}
                                                  label={nestedField.label}
                                                >
                                                  {nestedField.options.map((option, optIndex) => {
                                                    const isSelected = formValues[field.name][index][subField.name][subIndex][nestedField.name] === option;
                                                    return (
                                                      <MenuItem 
                                                        key={optIndex} 
                                                        value={option}
                                                        selected={isSelected}
                                                        sx={{
                                                          '&.Mui-selected': {
                                                            backgroundColor: '#1976d2',
                                                            color: '#ffffff',
                                                            fontWeight: 600,
                                                            '&:hover': {
                                                              backgroundColor: '#1565c0'
                                                            }
                                                          }
                                                        }}
                                                      >
                                                        {option}
                                                      </MenuItem>
                                                    );
                                                  })}
                                                </Select>
                                                {nestedField.helperText && (
                                                  <FormHelperText>{nestedField.helperText}</FormHelperText>
                                                )}
                                              </FormControl>
                                            ) : nestedField.type === 'date' ? (
                                              <TextField
                                                fullWidth
                                                label={nestedField.label}
                                                type="date"
                                                value={formValues[field.name][index][subField.name][subIndex][nestedField.name] || nestedField.defaultValue || ''}
                                                onChange={(e) => {
                                                  // Marquer le champ parent comme modifié
                                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                                  const newItems = [...formValues[field.name]];
                                                  const updatedSubItems = [...newItems[index][subField.name]];
                                                  updatedSubItems[subIndex] = {
                                                    ...updatedSubItems[subIndex],
                                                    [nestedField.name]: e.target.value
                                                  };
                                                  newItems[index] = {
                                                    ...newItems[index],
                                                    [subField.name]: updatedSubItems
                                                  };
                                                  setFormValues(prev => ({
                                                    ...prev,
                                                    [field.name]: newItems
                                                  }));
                                                }}
                                                margin="dense"
                                                required={nestedField.required}
                                                InputLabelProps={{ shrink: true }}
                                                size={nestedFieldSize}
                                                helperText={nestedField.helperText || ''}
                                                sx={{
                                                  '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                                    filter: isDark ? 'invert(1)' : 'none',
                                                    cursor: 'pointer'
                                                  }
                                                }}
                                              />
                                            ) : nestedField.type === 'datetime' ? (
                                              <TextField
                                                fullWidth
                                                label={nestedField.label}
                                                type="datetime-local"
                                                value={formValues[field.name][index][subField.name][subIndex][nestedField.name] || nestedField.defaultValue || ''}
                                                onChange={(e) => {
                                                  // Marquer le champ parent comme modifié
                                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                                  const newItems = [...formValues[field.name]];
                                                  const updatedSubItems = [...newItems[index][subField.name]];
                                                  updatedSubItems[subIndex] = {
                                                    ...updatedSubItems[subIndex],
                                                    [nestedField.name]: e.target.value
                                                  };
                                                  newItems[index] = {
                                                    ...newItems[index],
                                                    [subField.name]: updatedSubItems
                                                  };
                                                  setFormValues(prev => ({
                                                    ...prev,
                                                    [field.name]: newItems
                                                  }));
                                                }}
                                                margin="dense"
                                                required={nestedField.required}
                                                InputLabelProps={{ shrink: true }}
                                                size={nestedFieldSize}
                                                helperText={nestedField.helperText || ''}
                                                sx={{
                                                  '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                                                    filter: isDark ? 'invert(1)' : 'none',
                                                    cursor: 'pointer'
                                                  }
                                                }}
                                              />
                                            ) : nestedField.type === 'time' ? (
                                              <TextField
                                                fullWidth={nestedField.fullWidth !== false}
                                                label={nestedField.label}
                                                type="time"
                                                value={formValues[field.name][index][subField.name][subIndex][nestedField.name] || nestedField.defaultValue || ''}
                                                onChange={(e) => {
                                                  // Marquer le champ parent comme modifié
                                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                                  const newItems = [...formValues[field.name]];
                                                  const updatedSubItems = [...newItems[index][subField.name]];
                                                  updatedSubItems[subIndex] = {
                                                    ...updatedSubItems[subIndex],
                                                    [nestedField.name]: e.target.value
                                                  };
                                                  newItems[index] = {
                                                    ...newItems[index],
                                                    [subField.name]: updatedSubItems
                                                  };
                                                  setFormValues(prev => ({
                                                    ...prev,
                                                    [field.name]: newItems
                                                  }));
                                                }}
                                                margin="dense"
                                                required={nestedField.required}
                                                InputLabelProps={{ shrink: true }}
                                                size={nestedFieldSize}
                                                inputProps={{
                                                  step: 1 // Permet les secondes
                                                }}
                                                helperText={nestedField.helperText || ''}
                                                sx={{
                                                  '& input[type="time"]::-webkit-calendar-picker-indicator': {
                                                    cursor: 'pointer',
                                                    filter: 'invert(0.5)'
                                                  }
                                                }}
                                              />
                                            ) : nestedField.type === 'monthyear' ? (
                                              <TextField
                                                fullWidth
                                                label={nestedField.label}
                                                type="month"
                                                value={formValues[field.name][index][subField.name][subIndex][nestedField.name] || nestedField.defaultValue || ''}
                                                onChange={(e) => {
                                                  // Marquer le champ parent comme modifié
                                                  setModifiedFields(prev => new Set([...prev, field.name]));
                                                  const newItems = [...formValues[field.name]];
                                                  const updatedSubItems = [...newItems[index][subField.name]];
                                                  updatedSubItems[subIndex] = {
                                                    ...updatedSubItems[subIndex],
                                                    [nestedField.name]: e.target.value
                                                  };
                                                  newItems[index] = {
                                                    ...newItems[index],
                                                    [subField.name]: updatedSubItems
                                                  };
                                                  setFormValues(prev => ({
                                                    ...prev,
                                                    [field.name]: newItems
                                                  }));
                                                }}
                                                margin="dense"
                                                required={nestedField.required}
                                                InputLabelProps={{ shrink: true }}
                                                size={nestedFieldSize}
                                                helperText={nestedField.helperText || ''}
                                                sx={{
                                                  '& input[type="month"]::-webkit-calendar-picker-indicator': {
                                                    filter: isDark ? 'invert(1)' : 'none',
                                                    cursor: 'pointer'
                                                  }
                                                }}
                                              />
                                            ) : null}
                                          </Grid>
                                        );
                                      })}
                                    </Grid>
                                  </Paper>
                                ))
                              ) : (
                                <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                  Aucun élément ajouté
                                </Typography>
                              )}
                            </Box>
                          ) : null}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                Aucun élément ajouté
              </Typography>
            )}
            
            {hasError && (
              <Typography color="error" variant="caption">
                {formErrors[field.name]}
              </Typography>
            )}
          </Box>
        );
        
      case 'select':
        if (field.autocomplete || field.searchable || field.freeSolo) {
          return (
            <Box sx={{ position: 'relative', width: '100%' }}>
              {field.optional && <OptionalBadge isDark={isDark} />}
              <SearchableSelectField
                field={field}
                name={field.name}
                value={formValues[field.name] || (field.multiple ? [] : '')}
                hasError={hasError}
                helperText={hasError ? formErrors[field.name] : field.helperText}
                fieldSize={fieldSize}
                onValueChange={(nextValue) => {
                  handleInputChange({
                    target: {
                      name: field.name,
                      value: nextValue,
                      type: 'select'
                    }
                  }, field);
                }}
              />
            </Box>
          );
        }

        return (
          <Box sx={{ position: 'relative', width: '100%' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <FormControl
              fullWidth
              margin="dense"
              key={field.name}
              error={hasError}
              size={fieldSize}
              required={field.required}
            >
            <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
            <Select
              labelId={`${field.name}-label`}
              name={field.name}
              size={fieldSize}
              value={formValues[field.name] || (field.multiple ? [] : '')}
              multiple={field.multiple}
              renderValue={field.multiple ? selected => selected.join(', ') : undefined}
              onChange={(e) => handleInputChange(e, field)}
              label={field.label}
            >
              {field.options.map((option, index) => {
                // Vérifier si c'est un séparateur
                const isSeparator = isSeparatorOption(option);
                
                // Vérifier si l'option est sélectionnée
                const isSelected = field.multiple 
                  ? (formValues[field.name] || []).includes(option)
                  : formValues[field.name] === option;
                
                return (
                  <MenuItem 
                    key={index} 
                    value={option}
                    disabled={isSeparator}
                    sx={isSeparator ? { 
                      fontWeight: 'bold', 
                      backgroundColor: '#f5f5f5',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      cursor: 'default'
                    } : {}}
                  >
                    {field.multiple && !isSeparator && (
                      <Checkbox
                        checked={isSelected}
                          size={fieldSize === 'small' ? 'small' : 'medium'}
                        sx={{ mr: 1, py: 0 }}
                      />
                    )}
                    {option}
                  </MenuItem>
                );
              })}
            </Select>
            {(hasError || field.helperText) && (
              <FormHelperText>
                {hasError ? formErrors[field.name] : field.helperText}
              </FormHelperText>
            )}
            </FormControl>
          </Box>
        );
        
      case 'checkbox':
        return (
          <Box key={field.name} sx={{ position: 'relative' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <FormControlLabel
              control={
                <Checkbox
                  name={field.name}
                  checked={formValues[field.name] || false}
                  size={fieldSize === 'small' ? 'small' : 'medium'}
                  onChange={(e) => handleInputChange(e, field)}
                />
              }
              label={field.label}
            />
            {field.helperText && (
              <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
                {field.helperText}
              </FormHelperText>
            )}
          </Box>
        );
      
      case 'editor':
        return (
          <Box key={field.name} sx={{ mb: 1, position: 'relative' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <Typography variant="subtitle1" mb={0.5}>
              {field.label} {field.required && <span style={{color: 'red'}}>*</span>}
            </Typography>
            <RichTextEditor
              value={formValues[field.name] || ''}
              onChange={(value) => handleEditorChange(value, field.name)}
              placeholder={field.placeholder || 'Tapez votre texte ici...'}
              isDark={isDark}
            />
            {hasError ? (
              <Typography color="error" variant="caption">
                {formErrors[field.name]}
              </Typography>
            ) : field.helperText ? (
              <FormHelperText>
                {field.helperText}
              </FormHelperText>
            ) : null}
          </Box>
        );
        
      case 'insert': {
        const insertInputSize = resolveFieldSize(field, 'small');
        return (
          <Box key={field.name} sx={{ width: '100%', mb: 1, position: 'relative' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <Typography variant="subtitle1" mb={0.5}>
              {field.label} {field.required && <span style={{color: 'red'}}>*</span>}
            </Typography>
            {field.helperText && !hasError && (
              <FormHelperText sx={{ mt: -0.5, mb: 1 }}>
                {field.helperText}
              </FormHelperText>
            )}
            <Box sx={{ display: 'flex', mb: 1 }}>
              <TextField
                fullWidth
                value={insertValues[field.name] || ''}
                onChange={(e) => handleInsertChange(e, field.name)}
                placeholder={field.placeholder || 'Saisir un élément...'}
                variant="outlined"
                size={insertInputSize}
              />
              <IconButton 
                color="primary" 
                onClick={() => handleAddItem(field.name)}
                sx={{ ml: 1 }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ mt: 1, maxHeight: '200px', overflowY: 'auto' }}>
              {Array.isArray(formValues[field.name]) && formValues[field.name].length > 0 ? (
                <List dense>
                  {formValues[field.name].map((item, index) => (
                    <ListItem key={index} sx={{ py: 0, pr: 14, borderBottom: '1px solid #f0f0f0' }}>
                      <ListItemText primary={item} sx={{ pr: 2 }} />
                      <ListItemSecondaryAction>
                        <IconButton 
                          size="small"
                          onClick={() => handleMoveItemUp(field.name, index)}
                          disabled={index === 0}
                          sx={{ mr: 0.5 }}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleMoveItemDown(field.name, index)}
                          disabled={index === formValues[field.name].length - 1}
                          sx={{ mr: 0.5 }}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          edge="end" 
                          size="small"
                          onClick={() => handleRemoveItem(field.name, index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  Aucun élément dans la liste
                </Typography>
              )}
            </Box>
            
            {hasError && (
              <Typography color="error" variant="caption">
                {formErrors[field.name]}
              </Typography>
            )}
          </Box>
        );
      }
        
      case 'textarea':
        return (
          <Box sx={{ position: 'relative', width: '100%' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <TextField
              key={field.name}
              fullWidth
              multiline
              rows={4}
              label={field.label}
              name={field.name}
              value={formValues[field.name] || ''}
              onChange={(e) => handleInputChange(e, field)}
              margin="dense"
              size={fieldSize}
              error={hasError}
              helperText={hasError ? formErrors[field.name] : (field.helperText || '')}
              required={field.required}
            />
          </Box>
        );
        
      case 'datetime':
        return (
          <Box sx={{ position: 'relative', width: '100%' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <TextField
              key={field.name}
              fullWidth
              label={field.label}
              name={field.name}
              type="datetime-local"
              value={formValues[field.name] || ''}
              onChange={(e) => handleInputChange(e, field)}
              InputLabelProps={{ shrink: true }}
              margin="dense"
              size={fieldSize}
              error={hasError}
              helperText={hasError ? formErrors[field.name] : (field.helperText || '')}
              required={field.required}
              sx={{
                '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                  cursor: 'pointer'
                }
              }}
            />
          </Box>
        );
        
      case 'date':
        return (
          <Box sx={{ position: 'relative', width: '100%' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <TextField
              key={field.name}
              fullWidth
              label={field.label}
              name={field.name}
              type="date"
              value={formValues[field.name] || ''}
              onChange={(e) => handleInputChange(e, field)}
              InputLabelProps={{ shrink: true }}
              margin="dense"
              size={fieldSize}
              error={hasError}
              helperText={hasError ? formErrors[field.name] : (field.helperText || '')}
              required={field.required}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                  cursor: 'pointer'
                }
              }}
            />
          </Box>
        );
        
      case 'time':
        return (
          <Box sx={{ position: 'relative', width: '100%' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <TextField
              key={field.name}
              fullWidth={field.fullWidth !== false}
              label={field.label}
              name={field.name}
              type="time"
              value={formValues[field.name] || ''}
              onChange={(e) => handleInputChange(e, field)}
              InputLabelProps={{
                shrink: true,
                // Harmoniser le fond du label avec le thème pour éviter le bloc blanc en mode sombre
                style: { 
                  backgroundColor: isDark ? '#22303e' : '#ffffff', 
                  paddingLeft: 4, 
                  paddingRight: 4 
                }
              }}
              inputProps={{
                step: 60, // Intervalle de 1 minute
                style: {
                  fontSize: '16px',
                  padding: '16.5px 14px'
                }
              }}
              margin="dense"
              size={fieldSize}
              error={hasError}
              helperText={hasError ? formErrors[field.name] : (field.helperText || '')}
              required={field.required}
              sx={{
                ...customStyle,
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  cursor: 'pointer',
                  filter: 'invert(0.5)'
                }
              }}
            />
          </Box>
        );
        
      case 'monthyear':
        return (
          <Box sx={{ position: 'relative', width: '100%' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <TextField
              key={field.name}
              fullWidth
              label={field.label}
              name={field.name}
              type="month"
              value={formValues[field.name] || ''}
              onChange={(e) => handleInputChange(e, field)}
              InputLabelProps={{ shrink: true }}
              margin="dense"
              size={fieldSize}
              error={hasError}
              helperText={hasError ? formErrors[field.name] : (field.helperText || '')}
              required={field.required}
              sx={{
                '& input[type="month"]::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                  cursor: 'pointer'
                }
              }}
            />
          </Box>
        );
        
      case 'label':
        return (
          <Box key={field.name} sx={{ width: '100%', my: 2, position: 'relative', ...customStyle }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <Typography 
              variant={field.variant || 'subtitle1'} 
              color="primary"
              dangerouslySetInnerHTML={{ __html: field.label }}
            />
            {field.divider && <Divider sx={{ mt: 1 }} />}
          </Box>
        );
        
      default: // Texte simple
        return (
          <Box sx={{ position: 'relative', width: '100%' }}>
            {field.optional && <OptionalBadge isDark={isDark} />}
            <TextField
              key={field.name}
              fullWidth
              label={field.label}
              name={field.name}
              value={formValues[field.name] || ''}
              onChange={(e) => handleInputChange(e, field)}
              margin="dense"
              size={fieldSize}
              error={hasError}
              helperText={hasError ? formErrors[field.name] : (field.helperText || '')}
              required={field.required}
              sx={customStyle}
            />
          </Box>
        );
    }
  };
  
  // Afficher un indicateur de chargement
  if (loading && (!formFields || formFields.length === 0)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Afficher un message d'erreur
  if (loadError && (!formFields || formFields.length === 0)) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {loadError}
      </Alert>
    );
  }
  
  // Calculer la progression
  const progress = calculateProgress();
  
  return (
    <Paper elevation={0} sx={{ p: 2, mt: 2 }}>
      {/* Barre de progression */}
      {progress.total > 0 && (
        <Box sx={{ 
          mb: 2, 
          p: 1.5, 
          borderRadius: 1.5, 
          backgroundColor: isDark ? '#1e2a35' : '#f8f8f8',
          border: isDark ? '1px solid #2d3f4c' : '1px solid #e8e8e8'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              Progression de la génération
            </Typography>
            <Typography variant="caption" fontWeight="bold" color={progress.percentage === 100 ? 'success.main' : 'primary.main'}>
              {progress.filled} / {progress.total} champs obligatoires ({progress.percentage}%)
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress.percentage} 
            sx={{ 
              height: 4, 
              borderRadius: 0.5,
              backgroundColor: isDark ? '#2d3f4c' : '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                borderRadius: 0.5,
                backgroundColor: progress.percentage === 100 ? '#4caf50' : '#1976d2'
              }
            }}
          />
        </Box>
      )}
      
      {loadError && (
        <Alert severity="warning" sx={{ my: 2 }}>
          {loadError}
        </Alert>
      )}
      <form onSubmit={handleSubmit} autoComplete="off">
        <Grid container spacing={1}>
          {formFields.map((field, index) => {
            // Vérifier si le champ doit être affiché
            if (!shouldShowField(field)) {
              return null; // Ne pas rendre du tout l'élément Grid
            }
            
            // Vérifier s'il faut forcer une nouvelle ligne
            if (index > 0 && formFields[index-1].newLine) {
              return (
                <React.Fragment key={field.name}>
                  <Grid item xs={12}>
                    <Box sx={{ height: 8 }} />
                  </Grid>
                  <Grid item xs={getGridSize(field)}>
                    {renderFormField(field)}
                  </Grid>
                </React.Fragment>
              );
            }
            
            return (
              <Grid item xs={getGridSize(field)} key={field.name}>
                {renderFormField(field)}
              </Grid>
            );
          })}
        </Grid>
        
        {actionError && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 2,
              backgroundColor: isDark ? 'rgba(211, 47, 47, 0.15)' : undefined,
              color: isDark ? '#ff6b6b' : undefined,
              '& .MuiAlert-icon': {
                color: isDark ? '#ff6b6b' : undefined
              }
            }}
            action={
              <IconButton
                aria-label="fermer"
                color="inherit"
                size="small"
                onClick={() => {
                  setActionError(null);
                  setActionErrorDetails(null);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {actionError}
              </Typography>
              {actionErrorDetails && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
                  Détails : {actionErrorDetails}
                </Typography>
              )}
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Vos données ont été conservées. Vous pouvez réessayer ou modifier le formulaire.
              </Typography>
            </Box>
          </Alert>
        )}
        <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            disabled={previewLoading || generatingContent}
            sx={{ minWidth: 160 }}
          >
            Réinitialiser
          </Button>
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<AutoFixHighIcon />}
            disabled={generatingContent || previewLoading}
            sx={{ minWidth: 200, fontWeight: 'bold' }}
          >
            {generatingContent ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                Génération...
              </>
            ) : (
              'Générer'
            )}
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RemoveRedEyeIcon />}
            onClick={handlePreview}
            disabled={previewLoading || generatingContent}
            sx={{ minWidth: 160 }}
          >
            {previewLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Prévisualisation...
              </>
            ) : (
              'Prévisualiser'
            )}
          </Button>
        </Box>
      </form>
      
      {/* Box de prévisualisation */}
      {previewContent && (
        <Paper 
          elevation={2} 
          sx={{ 
            mt: 3, 
            p: 3, 
            backgroundColor: isDark ? '#1e2a35' : '#f9f9f9',
            border: isDark ? '1px solid #2d3f4c' : '1px solid #e0e0e0',
            position: 'relative'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: isDark ? '#90caf9' : 'primary.main'
              }}
            >
              {previewContent.title || 'Prévisualisation'}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setPreviewContent(null)}
              sx={{ color: isDark ? '#95a5a6' : 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Divider sx={{ 
            borderColor: isDark ? '#2d3f4c' : undefined,
            mb: 2 
          }} />
          
          {previewContent.content && (
            <Box 
              sx={{ 
                maxHeight: '400px',
                overflowY: 'auto',
                color: isDark ? '#ecf0f1' : 'inherit',
                '& p': { 
                  mb: 1,
                  color: isDark ? '#ecf0f1' : 'inherit'
                },
                '& ul, & ol': { 
                  ml: 2, 
                  mb: 1,
                  color: isDark ? '#ecf0f1' : 'inherit'
                },
                '& li': {
                  color: isDark ? '#ecf0f1' : 'inherit'
                },
                '& strong': { 
                  fontWeight: 600,
                  color: isDark ? '#ecf0f1' : 'inherit'
                },
                '& u': { 
                  textDecoration: 'underline',
                  color: isDark ? '#ecf0f1' : 'inherit'
                },
                // Styles pour les scrollbars en mode sombre
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: isDark ? '#1a252f' : '#f0f0f0',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: isDark ? '#2d3f4c' : '#c0c0c0',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: isDark ? '#384550' : '#a0a0a0',
                  },
                },
                // Pour Firefox
                scrollbarWidth: 'thin',
                scrollbarColor: isDark ? '#2d3f4c #1a252f' : '#c0c0c0 #f0f0f0',
              }}
              dangerouslySetInnerHTML={{ __html: previewContent.content }}
            />
          )}
        </Paper>
      )}
      
      {/* Dialog de confirmation de réinitialisation */}
      <Dialog
        open={resetDialogOpen}
        onClose={cancelReset}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#1e2a35' : '#ffffff',
            backgroundImage: 'none',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle 
          id="reset-dialog-title"
          sx={{ 
            color: isDark ? '#ecf0f1' : 'inherit',
            borderBottom: isDark ? '1px solid #2d3f4c' : '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <RestartAltIcon color="warning" />
          Réinitialiser le formulaire
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText 
            id="reset-dialog-description"
            sx={{ color: isDark ? '#bdc3c7' : 'text.secondary' }}
          >
            Êtes-vous sûr de vouloir réinitialiser le formulaire ? 
            <Box component="span" sx={{ display: 'block', mt: 1, fontWeight: 600, color: isDark ? '#e74c3c' : '#d32f2f' }}>
              Toutes les données saisies seront perdues et ne pourront pas être récupérées.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions 
          sx={{ 
            p: 2, 
            borderTop: isDark ? '1px solid #2d3f4c' : '1px solid #e0e0e0',
            gap: 1
          }}
        >
          <Button 
            onClick={cancelReset} 
            variant="outlined"
            sx={{ 
              color: isDark ? '#ecf0f1' : 'inherit',
              borderColor: isDark ? '#2d3f4c' : undefined
            }}
          >
            Annuler
          </Button>
          <Button 
            onClick={confirmReset} 
            variant="contained"
            color="warning"
            autoFocus
            startIcon={<RestartAltIcon />}
          >
            Réinitialiser
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default RubricForm;



